import { Briefcase, ChevronDown, ChevronRight, Dumbbell, GraduationCap, House, LocateFixed, MapPin, Plus, RefreshCw, Search, Trash2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Place, TravelMode } from '@tempo/core';
import { api } from '../api';
import { Button, Field, Loading, Modal, NumberInput, Switch } from '../components/ui';
import { MODE_LABEL } from '../lib/format';
import { currentPosition, useAction, usePlaces, useSettings } from '../lib/hooks';
import { toast } from '../lib/toast';

const KIND_ICON = { home: House, work: Briefcase, uni: GraduationCap, gym: Dumbbell, other: MapPin };

export function PlacesPage() {
  const places = usePlaces();
  const settings = useSettings().data;
  const [editing, setEditing] = useState<Place | 'new' | null>(null);
  const home = settings?.planner.homePlaceId;
  const campus = useQuery({ queryKey: ['campus'], queryFn: () => api.get<CampusInfo>('/campus') });
  const buildingIds = new Set((campus.data?.buildings ?? []).map((b) => b.placeId));
  const mine = (places.data ?? []).filter((p) => !buildingIds.has(p.id));
  const buildings = (places.data ?? []).filter((p) => buildingIds.has(p.id)).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Places</h1>
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus size={16} /> Add place
        </Button>
      </div>
      <p className="subtle small" style={{ marginBottom: 14 }}>
        Where things happen. Tempo uses these to work out journey times, when to leave and directions. Give each one an address or pin it with your
        location.
      </p>
      {places.isLoading ? (
        <Loading />
      ) : (
        <div className="stack">
          {mine.map((p) => {
            const Icon = KIND_ICON[p.kind] ?? MapPin;
            const located = p.lat != null && p.lng != null;
            return (
              <div key={p.id} className="card list-card" style={{ cursor: 'pointer' }} onClick={() => setEditing(p)}>
                <div style={{ color: 'var(--accent)', paddingTop: 2 }}>
                  <Icon size={20} />
                </div>
                <div className="grow">
                  <div className="row wrap" style={{ gap: 8 }}>
                    <span className="title">{p.name}</span>
                    {p.id === home && <span className="badge accent">Home</span>}
                    {p.travelMode && <span className="badge">{MODE_LABEL[p.travelMode]}</span>}
                  </div>
                  <div className="meta">
                    {p.address && <span>{p.address}</span>}
                    {!located && (
                      <span style={{ color: 'var(--warn)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <TriangleAlert size={13} /> No location – travel times can't be worked out
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <CampusBuildings buildings={buildings} missing={campus.data?.missing ?? []} onEdit={setEditing} />
        </div>
      )}
      {editing && <PlaceForm existing={editing === 'new' ? undefined : editing} isHome={editing !== 'new' && editing.id === home} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PlaceForm({ existing, isHome, onClose }: { existing?: Place; isHome: boolean; onClose: () => void }) {
  const [name, setName] = useState(existing?.name ?? '');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [lat, setLat] = useState<number | null>(existing?.lat ?? null);
  const [lng, setLng] = useState<number | null>(existing?.lng ?? null);
  const [kind, setKind] = useState<Place['kind']>(existing?.kind ?? 'other');
  const [mode, setMode] = useState<TravelMode | ''>(existing?.travelMode ?? '');
  const [makeHome, setMakeHome] = useState(isHome);
  const [results, setResults] = useState<{ label: string; lat: number; lng: number }[] | null>(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    setSearching(true);
    try {
      const r = await api.get<{ label: string; lat: number; lng: number }[]>(`/geocode?q=${encodeURIComponent(address)}`);
      setResults(r);
      if (!r.length) toast('No match – try adding the postcode', 'error');
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setSearching(false);
    }
  };
  const here = async () => {
    try {
      const pos = await currentPosition();
      setLat(Math.round(pos.coords.latitude * 1e6) / 1e6);
      setLng(Math.round(pos.coords.longitude * 1e6) / 1e6);
      toast(`Pinned to your location (±${Math.round(pos.coords.accuracy)} m)`);
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  const body = () => ({ name: name.trim(), address: address.trim() || null, lat, lng, kind, travelMode: mode || null, makeHome: makeHome && !isHome ? true : undefined });
  const save = useAction(() => (existing ? api.patch(`/places/${existing.id}`, body()) : api.post('/places', body())), 'Saved – travel times updated');
  const remove = useAction(() => api.del(`/places/${existing!.id}`), 'Place removed');

  return (
    <Modal
      title={existing ? `Edit ${existing.name}` : 'Add a place'}
      onClose={onClose}
      footer={
        <>
          {existing && !isHome && (
            <Button className="left" variant="danger" onClick={() => confirm(`Remove "${existing.name}"?`) && remove.mutateAsync().then(onClose).catch(() => undefined)}>
              <Trash2 size={16} /> Remove
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="Name">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="The Standing Order" />
          </Field>
          <Field label="Kind">
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as Place['kind'])}>
              <option value="home">Home</option>
              <option value="work">Work</option>
              <option value="uni">Uni / campus</option>
              <option value="gym">Gym</option>
              <option value="other">Other</option>
            </select>
          </Field>
        </div>
        <Field label="Address or postcode">
          <div className="row">
            <input className="input grow" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 30 High Street, Southampton SO14" onKeyDown={(e) => e.key === 'Enter' && search()} />
            <Button onClick={search} loading={searching} disabled={address.trim().length < 3}>
              <Search size={15} /> Find
            </Button>
          </div>
        </Field>
        {results && results.length > 0 && (
          <div className="stack tight">
            {results.map((r, i) => (
              <button
                key={i}
                className="card flat"
                style={{ textAlign: 'left', cursor: 'pointer', padding: 10, marginTop: 0, color: 'inherit', font: 'inherit' }}
                onClick={() => {
                  setLat(r.lat);
                  setLng(r.lng);
                  setResults(null);
                  toast('Location set');
                }}
              >
                <div className="small">{r.label}</div>
              </button>
            ))}
          </div>
        )}
        <div className="row wrap">
          <Button size="sm" onClick={here}>
            <LocateFixed size={14} /> Use where I am now
          </Button>
          {lat != null && lng != null ? (
            <a className="small" href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noreferrer">
              📍 {lat.toFixed(5)}, {lng.toFixed(5)} – check on map
            </a>
          ) : (
            <span className="small" style={{ color: 'var(--warn)' }}>
              Not located yet
            </span>
          )}
        </div>
        <details className="small">
          <summary className="faint">Enter coordinates manually</summary>
          <div className="grid-2" style={{ marginTop: 8 }}>
            <Field label="Latitude">
              <NumberInput value={lat} onChange={setLat} step={0.0001} />
            </Field>
            <Field label="Longitude">
              <NumberInput value={lng} onChange={setLng} step={0.0001} />
            </Field>
          </div>
        </details>
        <Field label="How you get here" hint="Overrides your default travel mode for trips to/from this place">
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value as TravelMode | '')}>
            <option value="">Default</option>
            {(['walk', 'cycle', 'transit', 'drive'] as TravelMode[]).map((m) => (
              <option key={m} value={m}>
                {MODE_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
        {!isHome && <Switch on={makeHome} onChange={setMakeHome} label="This is home" hint="Your day starts and ends here" />}
      </div>
    </Modal>
  );
}

interface CampusInfo {
  buildings: { code: string; placeId: string; name: string }[];
  missing: string[];
  lastRunAt: number | null;
}

/** University buildings Tempo found in the timetable – folded away so they don't crowd your own places. */
function CampusBuildings({ buildings, missing, onEdit }: { buildings: Place[]; missing: string[]; onEdit: (p: Place) => void }) {
  const [open, setOpen] = useState(false);
  const refresh = useAction(() => api.post<{ created: string[] }>('/campus/refresh'), (r) => (r.created.length ? `Found ${r.created.length} new building${r.created.length === 1 ? '' : 's'}` : 'Buildings up to date'));
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="row" style={{ padding: '12px 16px' }}>
        <button className="chunk-head" style={{ padding: 0, flex: 1 }} onClick={() => setOpen(!open)}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <GraduationCap size={18} style={{ color: 'var(--accent)' }} />
          <span>Campus buildings</span>
          <span className="faint">{buildings.length ? `${buildings.length} from your timetable` : 'none yet'}</span>
        </button>
        <Button size="sm" variant="ghost" icon onClick={() => refresh.mutate()} loading={refresh.isPending} aria-label="Look for new buildings">
          <RefreshCw size={15} />
        </Button>
      </div>
      {open && (
        <div>
          <p className="small subtle" style={{ padding: '0 16px 10px' }}>
            Found automatically from room codes like "35 / 1001", with exact locations from the University's open data – so journeys and
            directions go to the right building, on any campus.
          </p>
          {buildings.map((p) => (
            <div key={p.id} className="chunk-row" onClick={() => onEdit(p)}>
              <span className="chunk-text">
                <span className="chunk-title">{p.name}</span>
                <span className="chunk-sub">{p.address}</span>
              </span>
            </div>
          ))}
          {missing.length > 0 && (
            <div className="small" style={{ padding: '10px 16px', color: 'var(--warn)' }}>
              Couldn't find building{missing.length === 1 ? '' : 's'} {missing.join(', ')} – add {missing.length === 1 ? 'it' : 'them'} by hand and a location rule.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
